import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportAPI } from 'api/report';
import {
    ComponentSettings,
    ComponentSettingsKpiChart,
    ComponentsType,
    DisplayMask,
    IKpiChartVarianceSettings,
    KpiChartInitialDataResponse,
} from '@report-content';
import { ChartMetricsInfoType } from 'components/metric/MetricViewer';
import { useKpiChartSummaryStats } from './useKpiChartSummaryStats';
import { keepFirstRowPerDate } from '../series/keepFirstRowPerDate';

export interface KpiChartBlockRef {
    internalName: ComponentsType | null;
    settings: ComponentSettings | null;
}

// Shared by any consumer that only knows "which component is the report's designated
// summary block" (report viewer preview panel, dashboard tile info icon, ...): given that
// block, fetches its data (when it's a kpi_chart) and computes the same summary numbers
// KpiChart.tsx shows in its own inline summary panel.
export function useKpiChartBlockSummaryStats(
    block: KpiChartBlockRef | undefined,
    displayMasks: Array<DisplayMask>,
    elementId: number,
    segmentValueId: number,
): ChartMetricsInfoType | null {
    const kpiSettings =
        block?.internalName === 'kpi_chart' ? (block.settings as ComponentSettingsKpiChart | null) : null;
    const isSummaryEnabled = kpiSettings?.show_summary_in_viewer == 'Y';

    const { data: initialData } = useQuery<KpiChartInitialDataResponse, Error>({
        queryKey: [`kpi_chart_block_summary_${elementId}_${segmentValueId}_${kpiSettings?.uid}`],
        queryFn: () =>
            reportAPI.getComponentData(
                kpiSettings!,
                {
                    elementId,
                    segmentValueId,
                    context: 'view',
                    applyAccessMap: 'N',
                    forScreenShot: false,
                    pageSize: 0,
                    measurement_time: '',
                },
                null,
            ),
        enabled: isSummaryEnabled,
    });

    const measureFields = kpiSettings?.measureFields;
    const xAxisField = kpiSettings?.x_axis?.field;
    const rows = useMemo(
        () =>
            initialData
                ? keepFirstRowPerDate(initialData.dataResults.table, measureFields ?? [], xAxisField ?? '').rows
                : undefined,
        [initialData, measureFields, xAxisField],
    );

    const defaults = {
        data: [],
        kpiData: [],
        measureFields: [],
        xAxisField: '',
        kpiVariance: {
            includeKpiVariance: 'N',
            varianceCalcMethod: 'prior values',
            varianceCompareValuePeriod: 0,
            varianceCompareValueUnit: 'days',
            varianceTarget: null,
            showVarianceAs: 'percentage',
        } as IKpiChartVarianceSettings,
    };

    return useKpiChartSummaryStats({
        ...(isSummaryEnabled
            ? {
                  data: rows ?? defaults.data,
                  kpiData: initialData?.kpiResults ?? defaults.kpiData,
                  measureFields: measureFields ?? defaults.measureFields,
                  xAxisField: xAxisField ?? defaults.xAxisField,
                  kpiVariance: kpiSettings?.kpiVariance ?? defaults.kpiVariance,
                  kpiVarianceResult: initialData?.kpiVarianceResult,
              }
            : defaults),
        displayMasks,
        elementId,
    });
}
